import { useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Modal,
    ModalBody,
    ModalHeader,
    ModalContent,
    ModalCloseButton,
    ModalOverlay,
    useMediaQuery,
} from '@chakra-ui/react';

import DataGridWrapper from './DataGridWrapper';
import DataGridReadOnly from './DataGridReadOnly';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';

const DataSourceViewModal = ({
    onClose,
    fetchDataFunction,
    rightClickedRowId = null,
    primaryKeyName = null,
    ...rest
}) => {
    const [isDesktop] = useMediaQuery('(min-width: 769px)');
    const [dataGridRows, setDataGridRows] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [databaseTableName, setdatabaseTableName] = useState(null);
    const [initialPage, setInitialPage] = useState(0);
    const [targetRowIndexInPage, setTargetRowIndexInPage] = useState(null);

    const pageSize = 100; // Default page size

    const setColumns = (row) => {
        let columnDefinitions = [];
        for (let key in row) {
            columnDefinitions.push({
                field: key,
                headerTooltip: key.toUpperCase(),
            });
        }
        setColumnDefs(columnDefinitions);
    };

    const fetchData = useCallback(async () => {
        if (fetchDataFunction !== undefined) {
            const tableData = await fetchDataFunction();
            if (tableData?.dataBaseTableName) {
                setdatabaseTableName(tableData.dataBaseTableName);
            }

            if (tableData?.columnDefinitions?.length > 0) {
                setColumnDefs(tableData.columnDefinitions);
            } else if (tableData?.columnDefinitions === null || tableData?.columnDefinitions === undefined) {
                if (tableData?.rowData?.length > 0) {
                    setColumns(tableData.rowData[0]);
                } else if (tableData?.items?.length > 0) {
                    setColumns(tableData.items[0]);
                } else {
                    setColumnDefs([]);
                }
            }

            let rows = [];
            if (tableData?.rowData !== undefined) {
                rows = tableData.rowData;
            } else if (tableData?.items !== undefined) {
                rows = tableData.items;
            }

            setDataGridRows(rows);

            // Calculate initial page and row position to display the right-clicked row
            if (rows.length > 0) {
                let targetRowIndex = 0;

                // Find the row by primary key value
                if (rightClickedRowId !== null && primaryKeyName) {
                    // Find the index of the row with the matching primary key
                    const foundIndex = rows.findIndex((row) => {
                        const rowValue = row[primaryKeyName];
                        // Handle both string and number comparisons
                        return String(rowValue) === String(rightClickedRowId);
                    });

                    if (foundIndex !== -1) {
                        targetRowIndex = foundIndex;
                    } else {
                        targetRowIndex = 1;
                    }

                    // Calculate which page contains the target row
                    const calculatedPage = Math.floor(targetRowIndex / pageSize);

                    setInitialPage(calculatedPage);
                    setTargetRowIndexInPage(targetRowIndex);
                }
            }
        } else {
            setColumnDefs([]);
            setDataGridRows([]);
        }
        setIsLoading(false);
    }, [fetchDataFunction, rightClickedRowId, primaryKeyName]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Jump to the calculated page and scroll to the specific row once grid is ready
    const onGridReady = useCallback(
        (params) => {
            // Only proceed if we have a right-clicked row ID
            if (params.api && rightClickedRowId !== null) {
                // Pages are 0-indexed, so check if the desired page number is greater than 0
                if (initialPage !== null && initialPage > 0) {
                    params.api.paginationGoToPage(initialPage);
                }
                if (targetRowIndexInPage !== null && targetRowIndexInPage >= 0) {
                    params.api.ensureIndexVisible(targetRowIndexInPage, 'middle');
                }
            }
        },
        [initialPage, rightClickedRowId, targetRowIndexInPage]
    );

    return (
        <Modal isOpen onClose={onClose} size={isDesktop ? '6xl' : 'full'}>
            <ModalOverlay />
            <ModalContent pt={3} borderRadius={isDesktop ? '2xl' : 0}>
                <ModalHeader>
                    <FormattedMessage id="datasourceview_modal_title" />
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />
                <ModalBody>
                    {isLoading ? (
                        <div>
                            <FormattedMessage id="common_loading" />
                            <DefinitionsSkeleton />
                        </div>
                    ) : (
                        <div>
                            {databaseTableName != null ? (
                                <div style={{ marginBottom: '25px' }}>
                                    <h4>Table Name: {databaseTableName}</h4>
                                </div>
                            ) : null}
                            <DataGridWrapper>
                                <DataGridReadOnly
                                    columns={columnDefs}
                                    rowData={dataGridRows}
                                    onGridReady={onGridReady}
                                    paginationPageSize={pageSize}
                                />
                            </DataGridWrapper>
                        </div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DataSourceViewModal;
